import { vec2, vec3, vec4, mat4, quat } from "gl-matrix";
import { MathConst, Vec3Basics } from "./wContants.js";
import { MouseEventHandler, MouseButtons } from "./MouseEventHandler.js";
import { KeyboardEventHandler } from "./KeyboardEventHandler.js";
import { TouchEventHandler } from "./TouchEventHandler.js";
import { wRay } from "./wRay.js";
import { wMath } from "./wMath.js";

class wOrbitCamera {
    //protected
    constructor(name) {
        this.name_ = name;
        this.view_ = mat4.create();
        this.projection_ = mat4.create();
        this.jitterProjection_ = mat4.create();
        this.viewProjection_ = mat4.create();

        this.nearPlane_ = 0.5;
        this.farPlane_ = 512.0;
        this.fov_ = 60.0;
        this.viewPortWidth_ = 512.0;
        this.viewPortHeight_ = 512.0;
        this.aspectRatio_ = this.viewPortWidth_ / this.viewPortHeight_;

        // orbit
        this.pivot_ = vec3.fromValues(0.0, 0.0, 0.0);
        this.pivotTarget_ = vec3.fromValues(0.0, 0.0, 0.0);
        this.cameraUp_ = vec3.fromValues(0.0, 1.0, 0.0); // this is static
        this.cameraPosition_ = vec3.fromValues(0.0, 0.0, 10.0); // this is calculated on the fly from the euler angles

        this.moveThreshold_ = 0.001; // under this threshold the camera stops


        this.zoom_ = 100.0;
        this.zoomTarget_ = 100.0;
        this.minZoom_ = 1.0;
        this.yaw_ = 0.0;
        this.yawTarget_ = 0.0;

        this.pitch_ = 0.0;
        this.pitchTarget_ = 0.0;
        this.minPitch_ = -0.3;
        this.maxPitch_ = MathConst.fPI_HALF - 0.2;

        this.rotationrotStiffness_ = 1.0;
        this.moveStiffenss_ = 0.65;

        this.cameraRotationSpeed_ = 16.0;
        this.cameraMovementSpeed_ = 20.0;
        this.cameraZoomSpeed_ = 1.0;

        this.toYUpSpace_ = quat.create();
        this.toUpSpace_ = quat.create();

        this.jitterEnabled_ = false;
        this.jitterCount_ = 0;

        // test of half res rendering
        this.pixelX_ = 0;
        this.pixelY_ = 0;

        this.pixelSampleCount_ = [0, 0, 0, 0];

        this.cameraOnb_ = [
            vec3.fromValues(1.0, 0.0, 0.0),
            vec3.fromValues(0.0, 1.0, 0.0),
            vec3.fromValues(0.0, 0.0, 1.0),
        ];
    }

    setViewport(width, height) {
        this.viewPortWidth_ = width;
        this.viewPortHeight_ = height;
        this.aspectRatio_ = this.viewPortWidth_ / this.viewPortHeight_;
    }

    setNearAndFarPlane(near, far){
        this.nearPlane_ = near;
        this.farPlane_ = far;
     //   setPerspective(this.fov);
    }

    setPerspective(fovInDeg) {
        this.fov_ = fovInDeg;
        mat4.perspective(
            this.projection_,
            this.fov_ * (Math.PI / 180.0),
            this.aspectRatio_,
            this.nearPlane_,
            this.farPlane_
        );
    }

    setReversedZPerspective(fovInDeg) {
        this.fov_ = fovInDeg;
        this.projection_ = wMath.perspectiveZO(this.fov_ * (Math.PI / 180.0), this.aspectRatio_, this.nearPlane_, Infinity);
    }

    setOrbitPivot(pivot) {
        vec3.copy(this.pivot_, pivot);
    }

    setOrbitPivotTarget(pivot) {
        vec3.copy(this.pivotTarget_, pivot);
    }

    setCameraPosition(cameraPos) {
        vec3.copy(this.cameraPosition_, cameraPos);
        let toCamera = wMath.vec3Sub(cameraPos, this.pivot_);
        let yawPitch = wMath.cartesianToPolar(toCamera);
        this.zoomTarget_ = Math.max(this.minZoom_, vec3.length(toCamera));
        this.yawTarget_ = yawPitch[0];
        this.pitchTarget_ = yawPitch[1];
    }

    resetJitter() {
        this.jitterCount_ = 0;

        this.pixelX_ = 0;
        this.pixelY_ = 0;

        this.pixelSampleCount_[0] = 0;
        this.pixelSampleCount_[1] = 0;
        this.pixelSampleCount_[2] = 0;
        this.pixelSampleCount_[3] = 0;
    }

    getNearPlane(){
        return this.nearPlane_;
    }

    getFarPlane(){
        return this.farPlane_;
    }

    getJitterCount() {
        return this.jitterCount_;
    }

    getView() {
        return this.view_;
    }
    getProjection() {
        return this.projection_;
    }
    getViewProjection() {
        if (this.jitterEnabled_) {
            mat4.multiply(this.viewProjection_, this.jitterProjection_, this.view_);
        }
        else {
            mat4.multiply(this.viewProjection_, this.projection_, this.view_);
        }
        return this.viewProjection_;
    }

    setUpvector(upvec) {
        this.cameraUp_ = upvec;
        this.toYUpSpace_ = wMath.getQuatFromUnitVectors(this.cameraUp_, Vec3Basics.Y);
        quat.invert(this.toUpSpace_, this.toYUpSpace_); // perhaps only conjugation?
    }

    getFov(){
        return this.fov_;
    }

    getFovY(){
        return this.fov_ * this.aspectRatio_;
    }

    setZoomSpeed(speed) {
        this.cameraZoomSpeed_ = speed;
    }

    setZoom(zoom) {
        this.zoomTarget_ = zoom;
    }

    getRight() {
        return this.cameraOnb_[0];
    }

    getUp() {
        return this.cameraOnb_[1];
    }

    getDir() {
        return this.cameraOnb_[2];
    }
    
    getPosition() {
        return this.cameraPosition_;
    }

    getViewRay() {
        var viewRay = new wRay(this.getPosition(), this.getDir());
        return viewRay;
    }

    // input normalized mouse coordinates
    getPickingRay(fx, fy) {
        let ndcX = fx * 2.0 - 1.0;
        let ndcY = fy * 2.0 - 1.0;
        let invViewProj = mat4.create();
        let tmp = vec4.create();
        mat4.invert(invViewProj, this.viewProjection_);
        vec4.transformMat4(
            tmp,
            vec4.fromValues(ndcX, ndcY, 0.0, 1.0),
            invViewProj
        );

        const w = Math.max(0.001, tmp[3]);
        let worldPos = vec3.fromValues(tmp[0] / w, tmp[1] / w, tmp[2] / w);

        let dir = vec3.create();
        vec3.sub(dir, worldPos, this.getPosition());
        let normDir = vec3.create();
        vec3.normalize(normDir, dir);
        var Ray = new wRay(this.getPosition(), normDir);
        return Ray;
    }

    update(deltaTime, mouseEventHandler, keyboardEventHandler, touchEventHandler) {

        if (mouseEventHandler.isDown(MouseButtons.LeftButton) || touchEventHandler.isDown()) {

            let yawUpdate = 0.0;
            let pitchUpdate = 0.0;

            const deltaX = (touchEventHandler.isDown()) ? touchEventHandler.getDeltaX() : mouseEventHandler.getMouseDeltaX();
            const deltaY = (touchEventHandler.isDown()) ? touchEventHandler.getDeltaY() : mouseEventHandler.getMouseDeltaY();

            yawUpdate = -(deltaX * this.cameraRotationSpeed_ * deltaTime * this.aspectRatio_);
            pitchUpdate = (deltaY * this.cameraRotationSpeed_ * deltaTime);

            this.yawTarget_ += yawUpdate;

            if (this.yawTarget_ < 0.0) {
                this.yawTarget_ += MathConst.fTWO_PI;
                this.yaw_ += MathConst.fTWO_PI;
            }

            if (this.yawTarget_ > MathConst.fTWO_PI) {
                this.yawTarget_ -= MathConst.fTWO_PI;
                this.yaw_ -= MathConst.fTWO_PI;
            }

            this.pitchTarget_ += pitchUpdate;
        }

        if (mouseEventHandler.isDown(MouseButtons.RightButton)) {
            const pivotUpdateX =
                -mouseEventHandler.getMouseDeltaX() *
                this.cameraMovementSpeed_ *
                deltaTime *
                this.zoom_ *
                0.5 *
                this.aspectRatio_;
            const pivotUpdateY =
                mouseEventHandler.getMouseDeltaY() *
                this.cameraMovementSpeed_ *
                deltaTime *
                this.zoom_ *
                0.5;
            let tmpx = vec3.create();
            let tmpy = vec3.create();
            vec3.scale(tmpx, this.getRight(), pivotUpdateX);
            vec3.scale(tmpy, this.getUp(), pivotUpdateY);
            this.pivotTarget_[0] += tmpx[0] + tmpy[0];
            this.pivotTarget_[1] += tmpx[1] + tmpy[1];
            this.pivotTarget_[2] += tmpx[2] + tmpy[2];
        }

        if (Math.abs(mouseEventHandler.getMouseWheelDelta()) > 0) {

            let zoomFactor = Math.min(1.0, this.zoom_ / 300.0);
            // Mouse wheel deltaY is browserdependent.
            // Normalize to chrome where deltaY = 120.
            let zoomUpdate = 120*Math.sign(mouseEventHandler.getMouseWheelDelta()) * this.cameraZoomSpeed_ * deltaTime * zoomFactor;
            this.zoomTarget_ += zoomUpdate;
            this.zoomTarget_ = Math.max(this.minZoom_, this.zoomTarget_);
        }

        const rotStiffness = wMath.clampF(this.rotationrotStiffness_ * deltaTime, 0.0, 1.0);
        const moveStiffness = wMath.clampF(this.moveStiffenss_ * deltaTime, 0.0, 1.0);
      
        let pivotDelta = wMath.vec3Sub(this.pivotTarget_, this.pivot_);
       
        this.pivot_ = wMath.vec3Add(
            this.pivot_,
            wMath.vec3Scale(pivotDelta, moveStiffness)
        );

        let isUpdated =
            Math.abs(this.yawTarget_ - this.yaw_) > this.moveThreshold_ ||
            Math.abs(this.pitchTarget_ - this.pitch_) > this.moveThreshold_ ||
            Math.abs(this.zoomTarget_ - this.zoom_) > this.moveThreshold_ ||
            vec3.length(pivotDelta) > this.moveThreshold_;

        if (!isUpdated) {
            return isUpdated;
        }

        this.yaw_ += (this.yawTarget_ - this.yaw_) * rotStiffness;
        this.pitch_ += (this.pitchTarget_ - this.pitch_) * rotStiffness;
        this.pitch_ = wMath.clampF(this.pitch_, this.minPitch_, this.maxPitch_);
        this.zoom_ += (this.zoomTarget_ - this.zoom_) * rotStiffness;

        let tmp = wMath.polarToCartesian(this.yaw_, this.pitch_);
        let polarOffset = vec3.create(); // wMath.polarToCartesian(this.yaw_, this.pitch_);
        vec3.transformQuat(polarOffset, tmp, this.toUpSpace_);

        this.cameraPosition_ = wMath.vec3Add(
            this.pivot_,
            wMath.vec3Scale(polarOffset, this.zoom_)
        );

        mat4.lookAt(
            this.view_,
            this.cameraPosition_,
            this.pivot_,
            this.cameraUp_
        );

        // Z axis
        vec3.normalize(
            this.cameraOnb_[2],
            wMath.vec3Sub(this.cameraPosition_, this.pivot_)
        );

        let crossTmp = vec3.create();
        vec3.cross(crossTmp, this.cameraUp_, this.cameraOnb_[2]);
        vec3.normalize(this.cameraOnb_[0], crossTmp); // X
        vec3.cross(this.cameraOnb_[1], this.cameraOnb_[2], this.cameraOnb_[0]); // Y

        if (this.jitterEnabled_) {
            let hs = wMath.hammersley(this.jitterCount_ % 32, 32);
            let jitter = [hs[0] / this.viewPortWidth_, hs[1] / this.viewPortHeight_];

            let jitterMat = mat4.fromValues(
                1.0, 0.0, 0.0, 0.0,
                0.0, 1.0, 0.0, 0.0,
                0.0, 0.0, 1.0, 0.0,
                jitter[0] * 2.0, jitter[1] * 2.0, 0.0, 1.0);

            mat4.multiply(this.jitterProjection_, jitterMat, this.projection_);
        }

        if (isUpdated) {
            this.resetJitter();
        }

        return isUpdated;
    }

    updateJitterMatrix() {
        let pixelIndex = this.pixelX_ + this.pixelY_ * 2;
        let hs = wMath.hammersley(this.pixelSampleCount_[pixelIndex] % 32, 32);
        let jitter = [hs[0] / this.viewPortWidth_, hs[1] / this.viewPortHeight_];

        let jitterMat = mat4.fromValues(
            1.0, 0.0, 0.0, 0.0,
            0.0, 1.0, 0.0, 0.0,
            0.0, 0.0, 1.0, 0.0,
            jitter[0] * 2.0, jitter[1] * 2.0, 0.0, 1.0);

        mat4.multiply(this.jitterProjection_, jitterMat, this.projection_);
        let currentPixelIndex = this.pixelX_ + this.pixelY_ * 2;
        let reset = (currentPixelIndex == 0 && this.pixelSampleCount_[0] == 0);
        this.pixelSampleCount_[currentPixelIndex]++;
        return reset;
    }

    updateJitterIndex() {

        this.pixelX_++;
        if (this.pixelX_ > 1) {
            this.pixelX_ = 0;
            this.pixelY_++;

            if (this.pixelY_ > 1) {
                this.pixelX_ = 0;
                this.pixelY_ = 0;
            }
        }

        this.jitterCount_++;

    }
}

export { wOrbitCamera };
